// src/api/campaignExclusionService.js
// Campaign exclusion service for BespokeBridge.
// Handles frequency limits, unsubscribe exclusions, and recurring-campaign recurrence exclusions.
// Mirrors the BespokeBeauty campaignExclusionService but uses organizations/{orgId} paths
// and the resolveAudience helper for base targeting.

import {
  doc,
  getDoc,
  addDoc,
  collection,
  getDocs,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { resolveAudience, getContactOptIn } from './communicationsApi';

/**
 * Count deliveries per contact from the campaignDeliveries collection.
 * @returns {{ [contactId: string]: number }}
 */
const countDeliveriesFromCampaignCollection = async (orgId, campaignType, startTime) => {
  try {
    const deliveriesRef = collection(db, 'campaignDeliveries');
    const q = query(
      deliveriesRef,
      where('campaignType', '==', campaignType),
      where('orgId', '==', orgId),
      where('sentAt', '>=', Timestamp.fromDate(startTime))
    );
    const snap = await getDocs(q);
    const counts = {};
    snap.forEach(docSnap => {
      const cid = docSnap.data().contactId;
      if (cid) counts[cid] = (counts[cid] || 0) + 1;
    });
    return counts;
  } catch (err) {
    console.error('Error counting deliveries:', err);
    return {};
  }
};

/**
 * Get contacts after excluding those who have unsubscribed from this channel.
 */
export const getContactsAfterUnsubscribeExclusion = async (
  orgId,
  campaignType,
  targetingOptions
) => {
  const { audienceType, audienceFilters, selectedContacts, targetingType } = targetingOptions;
  const filters = targetingType === 'specific_contacts'
    ? { specificContactIds: (selectedContacts || []).map(c => c.id) }
    : (audienceFilters || {});
  const base = await resolveAudience(orgId, audienceType, filters, campaignType);

  // A contact is excluded when they have explicitly opted out of this channel.
  // getContactOptIn reads subscriptionPreferences.{email|pushNotifications|whatsApp}
  // and falls back correctly for WhatsApp legacy opt-in field.
  // Donors have no subscription preferences (they are email-only and never sign in), so they are never excluded here.
  const isUnsubscribed = (contact) => contact._type !== 'donor' && !getContactOptIn(contact, campaignType);

  const unsubscribed = [];
  const eligible = base.eligibleContacts.filter(c => {
    if (isUnsubscribed(c)) {
      unsubscribed.push({ ...c, exclusionReason: `Unsubscribed from ${campaignType} communications` });
      return false;
    }
    return true;
  });

  return {
    totalCount: base.totalCount,
    eligibleCount: eligible.length,
    eligibleCustomers: eligible,
    unsubscribedCount: unsubscribed.length,
    unsubscribedCustomers: unsubscribed,
  };
};

/**
 * Get contacts after excluding those who have hit frequency limits.
 */
export const getContactsAfterFrequencyExclusion = async (
  orgId,
  campaignType,
  targetingOptions
) => {
  const afterUnsub = await getContactsAfterUnsubscribeExclusion(orgId, campaignType, targetingOptions);

  const orgRef = doc(db, 'organizations', orgId);
  const orgDoc = await getDoc(orgRef);

  if (!orgDoc.exists()) {
    return { ...afterUnsub, frequencyExcludedCustomers: [], frequencyExcludedCount: 0 };
  }

  const frequencySettings = orgDoc.data().campaignFrequency?.[campaignType];

  if (!frequencySettings) {
    return { ...afterUnsub, frequencyExcludedCustomers: [], frequencyExcludedCount: 0 };
  }

  const now = new Date();
  let startTime;
  switch (frequencySettings.interval) {
    case 'day':   startTime = new Date(now - 24 * 60 * 60 * 1000); break;
    case 'week':  startTime = new Date(now - 7 * 24 * 60 * 60 * 1000); break;
    case 'month': startTime = new Date(now - 30 * 24 * 60 * 60 * 1000); break;
    default:      startTime = new Date(now - 7 * 24 * 60 * 60 * 1000);
  }

  const counts = await countDeliveriesFromCampaignCollection(orgId, campaignType, startTime);

  const frequencyExcluded = [];
  const eligible = afterUnsub.eligibleCustomers.filter(c => {
    const deliveryCount = counts[c.id] || 0;
    if (deliveryCount >= frequencySettings.maxFrequency) {
      frequencyExcluded.push({
        ...c,
        deliveryCount,
        limit: frequencySettings.maxFrequency,
        interval: frequencySettings.interval,
        exclusionReason: `Received ${deliveryCount} of ${frequencySettings.maxFrequency} allowed messages in the past ${frequencySettings.interval}`,
      });
      return false;
    }
    return true;
  });

  return {
    totalCount: afterUnsub.totalCount,
    eligibleCount: eligible.length,
    eligibleCustomers: eligible,
    unsubscribedCount: afterUnsub.unsubscribedCount,
    unsubscribedCustomers: afterUnsub.unsubscribedCustomers,
    frequencyExcludedCount: frequencyExcluded.length,
    frequencyExcludedCustomers: frequencyExcluded,
    frequencySettings,
  };
};

/**
 * Get contacts after excluding previous recipients (for recurring campaigns).
 */
export const getContactsAfterRecurrenceExclusion = async (
  orgId,
  campaignType,
  targetingOptions,
  recurringSettings
) => {
  const afterFreq = await getContactsAfterFrequencyExclusion(orgId, campaignType, targetingOptions);

  if (!recurringSettings?.excludePreviousRecipients) {
    return { ...afterFreq, recurrenceExcludedCount: 0, recurrenceExcludedCustomers: [] };
  }

  let cutoffDate;
  if (recurringSettings.exclusionType === 'date') {
    cutoffDate = new Date(recurringSettings.excludeBeforeDate);
  } else {
    const cyclesBack = recurringSettings.excludePastCycles || 1;
    const now = new Date();
    switch (recurringSettings.frequency) {
      case 'daily':    cutoffDate = new Date(now - cyclesBack * 24 * 60 * 60 * 1000); break;
      case 'weekly':   cutoffDate = new Date(now - cyclesBack * 7 * 24 * 60 * 60 * 1000); break;
      case 'monthly':  cutoffDate = new Date(now - cyclesBack * 30 * 24 * 60 * 60 * 1000); break;
      case 'annually': cutoffDate = new Date(now - cyclesBack * 365 * 24 * 60 * 60 * 1000); break;
      default:         cutoffDate = new Date(now - 7 * 24 * 60 * 60 * 1000);
    }
  }

  const deliveriesRef = collection(db, 'campaignDeliveries');
  const q = query(
    deliveriesRef,
    where('campaignType', '==', campaignType),
    where('orgId', '==', orgId),
    where('sentAt', '>=', Timestamp.fromDate(cutoffDate))
  );
  const snap = await getDocs(q);
  const previousIds = new Set();
  snap.forEach(d => previousIds.add(d.data().contactId));

  const recurrenceExcluded = [];
  const eligible = afterFreq.eligibleCustomers.filter(c => {
    if (previousIds.has(c.id)) {
      recurrenceExcluded.push({ ...c, exclusionReason: 'Received this campaign in a previous cycle' });
      return false;
    }
    return true;
  });

  return {
    ...afterFreq,
    eligibleCount: eligible.length,
    eligibleCustomers: eligible,
    recurrenceExcludedCount: recurrenceExcluded.length,
    recurrenceExcludedCustomers: recurrenceExcluded,
    recurringSettings,
  };
};

/**
 * Final nett list after all exclusions.
 */
export const getFinalContactList = async (
  orgId,
  campaignType,
  targetingOptions,
  recurringSettings = null
) => {
  const result = await getContactsAfterRecurrenceExclusion(
    orgId,
    campaignType,
    targetingOptions,
    recurringSettings
  );

  const totalExcluded =
    (result.unsubscribedCount || 0) +
    (result.frequencyExcludedCount || 0) +
    (result.recurrenceExcludedCount || 0);

  const afterTargeting =
    result.eligibleCount +
    (result.unsubscribedCount || 0) +
    (result.frequencyExcludedCount || 0) +
    (result.recurrenceExcludedCount || 0);

  const exclusionRate = afterTargeting > 0
    ? ((totalExcluded / afterTargeting) * 100).toFixed(1)
    : '0.0';

  return {
    ...result,
    summary: {
      totalCustomers: result.totalCount,
      afterTargeting,
      excludedByUnsubscribe: result.unsubscribedCount || 0,
      excludedByFrequency: result.frequencyExcludedCount || 0,
      excludedByRecurrence: result.recurrenceExcludedCount || 0,
      totalExcluded,
      finalCount: result.eligibleCount,
      exclusionRate,
    },
  };
};

/**
 * Record campaign deliveries after a blast.
 */
export const recordCampaignDeliveries = async (orgId, campaignType, contacts) => {
  try {
    const ref = collection(db, 'campaignDeliveries');
    await Promise.all(contacts.map(c =>
      addDoc(ref, { contactId: c.id, orgId, campaignType, sentAt: Timestamp.now() })
    ));
  } catch (err) {
    console.error('Error recording campaign deliveries (non-blocking):', err);
  }
};

/**
 * Apply frequency exclusion to a contact list before sending a blast.
 */
export const applyFrequencyExclusionToList = async (orgId, campaignType, contacts) => {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    const orgDoc = await getDoc(orgRef);
    if (!orgDoc.exists()) return { eligible: contacts, excluded: [] };

    const freq = orgDoc.data().campaignFrequency?.[campaignType];
    if (!freq?.maxFrequency) return { eligible: contacts, excluded: [] };

    const now = new Date();
    let startTime;
    switch (freq.interval) {
      case 'day':   startTime = new Date(now - 24 * 60 * 60 * 1000); break;
      case 'week':  startTime = new Date(now - 7 * 24 * 60 * 60 * 1000); break;
      case 'month': startTime = new Date(now - 30 * 24 * 60 * 60 * 1000); break;
      default:      startTime = new Date(now - 7 * 24 * 60 * 60 * 1000);
    }

    const counts = await countDeliveriesFromCampaignCollection(orgId, campaignType, startTime);

    const eligible = [];
    const excluded = [];
    contacts.forEach(c => {
      const n = counts[c.id] || 0;
      if (n >= freq.maxFrequency) {
        excluded.push({ ...c, deliveryCount: n, limit: freq.maxFrequency, interval: freq.interval });
      } else {
        eligible.push(c);
      }
    });
    return { eligible, excluded };
  } catch (err) {
    console.error('Error applying frequency exclusion:', err);
    return { eligible: contacts, excluded: [] };
  }
};
